import { useState } from "react";
import { Plus, Pencil, Trash2, Link as LinkIcon, MapPin, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, fileUrl } from "@/lib/api";
import { Button } from "@/components/Button";
import { importantLabel } from "@/lib/profileLabels";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";
import { ImageUpload } from "@/components/ImageUpload";
import { EmptyState } from "@/components/common";

// Generic manager for profile_items in a category.
export function ProfileCategory({ category, subOptions = [], items, reload, emptyIcon, emptyText, rich = false, examples = {}, topics = [] }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const subLabel = (v) => subOptions.find((o) => o.value === v)?.label || v;
  const isCustom = (v) => v === "custom" || v === "other";
  const groupKey = (it) => (isCustom(it.subcategory) && it.data?.custom_type?.trim()) ? `custom:${it.data.custom_type.trim()}` : it.subcategory;
  const topicLabel = (d) => (d?.topic === "custom" ? d?.custom_topic?.trim() : topics.find((o) => o.value === d?.topic)?.label);
  const groupLabel = (k) => (k.startsWith("custom:") ? k.slice(7) : subLabel(k));

  const startAdd = () => {
    setEditing(null);
    setForm({ subcategory: subOptions[0]?.value || "", name: "", detail: "", data: {} });
    setOpen(true);
  };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ subcategory: it.subcategory, name: it.name, detail: it.detail || "", data: it.data || {} });
    setOpen(true);
  };

  const showRich = rich && ["places", "restaurants", "stores"].includes(form.subcategory);

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Please enter a name.");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/profile/items/${editing.id}`, {
          subcategory: form.subcategory, name: form.name, detail: form.detail, data: form.data,
        });
      } else {
        await api.post(`/profile/items`, {
          category, subcategory: form.subcategory, name: form.name, detail: form.detail, data: form.data,
        });
      }
      setOpen(false);
      await reload();
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (it) => {
    try {
      await api.delete(`/profile/items/${it.id}`);
      await reload();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  // group by subcategory
  const groups = {};
  items.forEach((it) => (groups[groupKey(it)] = groups[groupKey(it)] || []).push(it));

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button variant="secondary" onClick={startAdd} data-testid={`add-${category}-btn`} className="!py-2">
          <Plus size={16} /> Add
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={emptyIcon} title={emptyText} subtitle="Tap Add to save your first entry." />
      ) : (
        <div className="space-y-4">
          {Object.entries(groups).map(([sub, list]) => (
            <div key={sub}>
              {subOptions.length > 1 && (
                <p className="text-xs font-semibold uppercase tracking-wider text-sky-400/80 mb-2">{groupLabel(sub)}</p>
              )}
              <div className="space-y-2">
                {list.map((it) => (
                  <div key={it.id} className="iks-card p-3.5 flex items-start gap-3 group" data-testid="profile-item">
                    {it.data?.photo && (
                      <img src={fileUrl(it.data.photo)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-100 font-medium text-sm flex flex-wrap items-center gap-1.5">
                        <span className="break-words" data-testid="item-name">
                          {category === "important" && <span className="text-sky-300 font-semibold" data-testid="item-type-label">{importantLabel(it)}: </span>}
                          {it.name}
                        </span>
                        {topicLabel(it.data) && (
                          <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[11px] text-slate-400 font-normal" data-testid="item-topic-chip">{topicLabel(it.data)}</span>
                        )}
                      </p>
                      {it.detail && <p className="text-slate-400 text-sm mt-0.5 whitespace-pre-line break-words" data-testid="item-detail-text">{it.detail}</p>}
                      {it.data?.order && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {it.data.order.split("\n").map((o) => o.trim()).filter(Boolean).map((o, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-200 text-xs">
                              <UtensilsCrossed size={11} className="text-sky-400/80 shrink-0" />
                              {o}
                            </span>
                          ))}
                        </div>
                      )}
                      {it.data?.notes && <p className="text-slate-500 text-xs mt-1 whitespace-pre-line break-words">{it.data.notes}</p>}
                      {it.data?.link && (
                        <a href={it.data.link} target="_blank" rel="noreferrer" className="text-xs text-sky-400 inline-flex items-center gap-1 mt-1">
                          <LinkIcon size={12} /> Link
                        </a>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(it)} className="p-1.5 text-slate-400 hover:text-sky-400" data-testid="edit-item-btn">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => del(it)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid="delete-item-btn">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit entry" : "Add entry"}
        testId="profile-item-modal"
        footer={
          <>
            <Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button>
            <Button full loading={busy} onClick={save} data-testid="save-item-btn">Save</Button>
          </>
        }
      >
        {subOptions.length > 1 && (
          <Field label="Type">
            <select
              value={form.subcategory}
              onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))}
              className="iks-input w-full px-3.5 py-2.5 text-sm"
              data-testid="item-subcategory"
            >
              {subOptions.map((o) => (
                <option key={o.value} value={o.value} className="bg-slate-900">{o.label}</option>
              ))}
            </select>
          </Field>
        )}
        {isCustom(form.subcategory) && (
          <Field label="Custom type" hint="Name your own type, e.g. Coffee Shops, Bakeries, Pet Peeves">
            <TextInput value={form.data?.custom_type || ""} onChange={(e) => setForm((f) => ({ ...f, data: { ...f.data, custom_type: e.target.value } }))} placeholder="e.g. Coffee Shops" data-testid="item-custom-type" />
          </Field>
        )}
        {topics.length > 0 && (
          <Field label="Subcategory (optional)">
            <select
              value={form.data?.topic || ""}
              onChange={(e) => setForm((f) => ({ ...f, data: { ...f.data, topic: e.target.value } }))}
              className="iks-input w-full px-3.5 py-2.5 text-sm"
              data-testid="item-topic"
            >
              <option value="" className="bg-slate-900">None</option>
              {topics.map((o) => (
                <option key={o.value} value={o.value} className="bg-slate-900">{o.label}</option>
              ))}
            </select>
          </Field>
        )}
        {topics.length > 0 && form.data?.topic === "custom" && (
          <Field label="Custom subcategory">
            <TextInput value={form.data?.custom_topic || ""} onChange={(e) => setForm((f) => ({ ...f, data: { ...f.data, custom_topic: e.target.value } }))} placeholder="e.g. Board games" data-testid="item-custom-topic" />
          </Field>
        )}
        <Field label={showRich ? "Place name" : "Name"}>
          <TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={(examples[form.subcategory]?.name) || (showRich ? "e.g. Subway" : "e.g. add an entry")} data-testid="item-name" />
        </Field>
        {showRich ? (
          <>
            <Field label="Favorite orders" hint="Add one per line to keep separate orders organized">
              <TextArea value={form.data?.order || ""} onChange={(e) => setForm((f) => ({ ...f, data: { ...f.data, order: e.target.value } }))} placeholder={examples[form.subcategory]?.detail || "Italian BMT on wheat\nHot chocolate, no whip"} data-testid="item-order" />
            </Field>
            <Field label="Notes">
              <TextArea value={form.data?.notes || ""} onChange={(e) => setForm((f) => ({ ...f, data: { ...f.data, notes: e.target.value } }))} placeholder="Toppings, extras, anything to remember" />
            </Field>
            <Field label="Link (optional)">
              <TextInput value={form.data?.link || ""} onChange={(e) => setForm((f) => ({ ...f, data: { ...f.data, link: e.target.value } }))} placeholder="https://" />
            </Field>
            <Field label="Photo (optional)">
              <ImageUpload variant="wide" value={form.data?.photo} onChange={(p) => setForm((f) => ({ ...f, data: { ...f.data, photo: p } }))} />
            </Field>
          </>
        ) : (
          <Field label="Detail (optional)">
            <TextArea value={form.detail} onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))} placeholder={examples[form.subcategory]?.detail || "Add a note or value"} data-testid="item-detail" />
          </Field>
        )}
      </Modal>
    </div>
  );
}
